import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);     // null=unauth, object=authed
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Always call /auth/me — auth is now cookie-based (httpOnly). Legacy
    // sessions with a token in localStorage are also handled: the axios
    // request interceptor attaches the Bearer header when present.
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => { localStorage.removeItem("jspa_token"); })
      .finally(() => setLoading(false));
  }, []);

  async function login(phone, password) {
    // Backend sets the httpOnly `jspa_token` cookie on this response.
    // We do NOT persist the returned token to localStorage: keeping JWTs
    // out of JS-accessible storage is the whole point of this migration.
    const { data } = await api.post("/auth/login", { phone, password });
    // Clear any legacy token from prior sessions so we don't send both.
    localStorage.removeItem("jspa_token");
    setUser(data.user);
    return data.user;
  }

  async function logout() {
    try {
      // Ask the server to clear the httpOnly cookie. Best-effort — even if
      // it fails (e.g. offline), we still forget the user client-side.
      await api.post("/auth/logout");
    } catch { /* ignore */ }
    localStorage.removeItem("jspa_token");
    setUser(null);
  }

  const value = useMemo(
    () => ({ user, loading, login, logout, setUser }),
    [user, loading]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
